// Where you are, how to find something, and how to look at it. While items are dragged, each way
// back up the trail is also a place to drop them, for moving them out of the folder on screen.
import classnames from 'classnames'
import PropTypes from 'prop-types'
import {Link} from 'react-router-dom'
import {useState} from 'react'

import Avatar from '@mui/material/Avatar'
import ChevronIcon from '@mui/icons-material/ChevronRightRounded'
import Divider from '@mui/material/Divider'
import Fade from '@mui/material/Fade'
import GridIcon from '@mui/icons-material/GridViewRounded'
import IconButton from '@mui/material/IconButton'
import ListIcon from '@mui/icons-material/ViewListRounded'
import Menu from '@mui/material/Menu'
import MenuItem from '@mui/material/MenuItem'
import SignOutIcon from '@mui/icons-material/LogoutRounded'
import SearchIcon from '@mui/icons-material/SearchRounded'

import useItemDrop from '../../lib/hooks/useItemDrop'

import './driveTopbar.scss'

const VIEWS = [
  {id: 'list', label: 'List view', Icon: ListIcon},
  {id: 'grid', label: 'Grid view', Icon: GridIcon}
]

const initials = ({firstName, lastName}) => `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase()

const SECTION_NAMES = {drive: 'My Drive', recent: 'Recent', starred: 'Starred', trash: 'Trash', locked: 'Locked'}

// Where a trail starts, and where the folders on it lead: the top of the drive, or of Locked.
const TOPS = {
  drive: {to: '/', folders: '/folders'},
  locked: {to: '/locked', folders: '/locked/folders'}
}

const folderShape = PropTypes.shape({id: PropTypes.string.isRequired, name: PropTypes.string.isRequired})

const Crumb = ({to, name, onDrop}) => {
  const [over, dropHandlers] = useItemDrop(onDrop)

  return (
    <>
      <Link className={classnames('crumb', {'drop-target': over})} to={to} {...dropHandlers}>{name}</Link>
      <ChevronIcon className='crumb-separator' />
    </>
  )
}

Crumb.propTypes = {
  to: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
  onDrop: PropTypes.func
}

// A folder is reached through the top it is under — My Drive or Locked — and every folder above
// it, each a way back up.
const Crumbs = ({trail, section, onDropInto}) => {
  const folder = trail.at(-1)
  const top = TOPS[section] ?? TOPS.drive
  const dropInto = place => onDropInto && (() => onDropInto(place))

  return (
    <nav className='topbar-crumbs' aria-label='Breadcrumb'>
      {folder && <Crumb to={top.to} name={SECTION_NAMES[section] ?? SECTION_NAMES.drive} onDrop={dropInto(null)} />}
      {trail.slice(0, -1).map(entry => (
        <Crumb key={entry.id} to={`${top.folders}/${entry.id}`} name={entry.name} onDrop={dropInto(entry)} />
      ))}
      <span className='crumb current' aria-current='page'>{folder?.name || SECTION_NAMES[section]}</span>
    </nav>
  )
}

Crumbs.propTypes = {
  trail: PropTypes.arrayOf(folderShape).isRequired,
  section: PropTypes.oneOf(Object.keys(SECTION_NAMES)).isRequired,
  onDropInto: PropTypes.func
}

const userShape = PropTypes.shape({
  firstName: PropTypes.string.isRequired,
  lastName: PropTypes.string.isRequired
})

const AccountMenu = ({user, onSignOut}) => {
  const [menuAnchor, setMenuAnchor] = useState(null)
  const name = `${user.firstName} ${user.lastName}`

  return (
    <>
      <IconButton
        className='topbar-account'
        aria-label={`Account: ${name}`}
        aria-haspopup='menu'
        aria-expanded={Boolean(menuAnchor)}
        title={name}
        onClick={event => setMenuAnchor(event.currentTarget)}>
        <Avatar className='topbar-avatar'>{initials(user)}</Avatar>
      </IconButton>
      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={() => setMenuAnchor(null)}
        anchorOrigin={{vertical: 'bottom', horizontal: 'right'}}
        transformOrigin={{vertical: 'top', horizontal: 'right'}}
        slots={{transition: Fade}}
        transitionDuration={0}
        slotProps={{paper: {className: 'drive-menu account-menu'}}}>
        <li className='menu-account'>{name}</li>
        <Divider />
        <MenuItem className='menu-danger' onClick={onSignOut}><SignOutIcon />Sign out</MenuItem>
      </Menu>
    </>
  )
}

AccountMenu.propTypes = {
  user: userShape.isRequired,
  onSignOut: PropTypes.func.isRequired
}

const DriveTopbar = ({trail, section, query, onQueryChange, view, onViewChange, user, onSignOut, onDropInto}) => (
  <header className='drive-topbar'>
    <Crumbs trail={trail} section={section} onDropInto={onDropInto} />

    <label className='topbar-search'>
      <SearchIcon />
      {/* Kept out of the browser's form history and away from spellcheckers, which post what
          they check: while Locked is hidden, its passphrase is typed in here. */}
      <input
        type='text'
        aria-label='Search this drive'
        placeholder='Search Drive'
        value={query}
        onChange={event => onQueryChange(event.target.value)}
        autoComplete='off'
        autoCorrect='off'
        autoCapitalize='off'
        spellCheck={false}
      />
    </label>

    <div className='topbar-views' role='group' aria-label='View'>
      {VIEWS.map(({id, label, Icon}) => (
        <IconButton
          key={id}
          className={classnames('view-option', {on: view === id})}
          aria-label={label}
          aria-pressed={view === id}
          title={label}
          onClick={() => onViewChange(id)}>
          <Icon fontSize='small' />
        </IconButton>
      ))}
    </div>

    {user && <AccountMenu user={user} onSignOut={onSignOut} />}
  </header>
)

DriveTopbar.propTypes = {
  // The folder on screen and those above it, the top first. Empty outside a folder.
  trail: PropTypes.arrayOf(folderShape).isRequired,
  section: PropTypes.string.isRequired,
  query: PropTypes.string.isRequired,
  onQueryChange: PropTypes.func.isRequired,
  view: PropTypes.oneOf(['list', 'grid']).isRequired,
  onViewChange: PropTypes.func.isRequired,
  user: userShape,
  onSignOut: PropTypes.func.isRequired,
  // While items are dragged: handed the folder of a crumb they are dropped on, or null for My Drive.
  onDropInto: PropTypes.func
}

export default DriveTopbar
